/**
 * The screener as a list of phone-sized rows.
 *
 * Symbol and name on the left, two numbers on the right, 56px tall so a thumb
 * can hit one. It replaces the AG Grid panel and nothing else: the group bar
 * above it, the socket, the selection and the filters are all still
 * `ScreenerView`'s — see its `renderRows` prop.
 *
 * The grid itself was the other option and is the wrong one at this width.
 * Twenty columns on a 390px screen is a horizontal scroll for every number,
 * and the two that matter are then never both on screen with the symbol.
 *
 * Everything decidable — which columns, which rows, in what order, formatted
 * how, coloured with which token — is `lib/mobile-rows.ts`, which is testable.
 * This is markup and a tap handler.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ScreenerConfig } from "@qufolio/model";
import type { ScreenerColumnMeta, ScreenerFrame } from "@qufolio/transport";

import {
  columnPages,
  detailFrame,
  mobileRows,
  searchRows,
  MOBILE_COLUMNS_PER_LINE,
  TABLET_COLUMNS_PER_LINE,
  type MobileRow,
} from "../lib/mobile-rows";
import { useTokenResolver } from "./theme";

export interface MobileRowsProps {
  frames: readonly ScreenerFrame[];
  config: ScreenerConfig;
  meta: readonly ScreenerColumnMeta[];
  /** A row was tapped — chart it. */
  onSelectSymbol?: (symbol: string) => void;
  /** The symbol on the chart, marked so the list says where you are. */
  chartedSymbol?: string;
  /** The rows on screen, in order, lifted for the chart's symbol wheel. */
  onSymbols?: (symbols: { symbol: string; name?: string }[]) => void;
  /** The touch screener's search box: narrows what is drawn, never the filter. */
  query?: string;
  /** What the conditions kept, of how many rows the universe has. */
  onRowCounts?: (matched: number, total: number) => void;
  /** Nothing here matches the search: look in every symbol instead. */
  onSearchAll?: (query: string) => void;
  /** An iPad: six figures a line instead of three. */
  wide?: boolean;
}

const LIST: React.CSSProperties = {
  /**
   * `height: 100%`, **not** `flex: 1 1 auto`.
   *
   * `ScreenerView` puts its body in a `<div style={{flex:"1 1 auto",minHeight:0}}>`
   * — a flex *item*, not a flex *container*. So a `flex` value here matches
   * nothing, the height falls back to the content, and five hundred rows
   * overflow a box that has no `overflow` of its own: the **whole view**
   * scrolls, carrying the group bar off the top of the screen.
   *
   * That is what "the chips are clipped" was. The bar was fine; it had been
   * scrolled away by a list that was never told how tall it was. AG Grid never
   * hit it because it sizes itself to 100% for its own reasons.
   */
  height: "100%",
  overflowY: "auto",
  // Momentum scrolling. Without it a long list feels stuck to the finger on iOS.
  WebkitOverflowScrolling: "touch",
  /**
   * **`pan-y`: the browser owns vertical, this owns horizontal.**
   *
   * Left at the default `auto`, the browser claims *both* axes and fires
   * `pointercancel` the moment a drag starts — so a horizontal swipe was
   * cancelled before the handler could classify it, and the gesture simply
   * never fired. Nothing was wrong with the paging; the events were gone.
   *
   * `none` was the other option and is the wrong one: it takes vertical
   * scrolling away from five hundred rows to buy an occasional swipe. `pan-y`
   * keeps the scroll native — momentum, rubber-banding and all — and releases
   * only the axis the list does not use.
   */
  touchAction: "pan-y",
};

/**
 * One number's track, in pixels.
 *
 * Two of these plus the gap is ~186px of the 390 a phone gives, leaving the
 * symbol and company name the rest. Wide enough for a five-figure price with a
 * short label beside it; anything longer is ellipsised rather than allowed to
 * move its neighbour.
 */
const CELL_WIDTH = 66;

/**
 * The logo's box, spanning both of the row's lines.
 *
 * 34px against a 56px row: large enough to recognise a mark at arm's length,
 * small enough that the three number tracks beside it still fit 390px —
 * 6 (dot) + 34 (logo) + ~90 (symbol) + 3x66 + gaps.
 */
const LOGO_SIZE = 34;

/** How far a horizontal drag must travel to count as a page turn. */
const SWIPE_DISTANCE = 40;

/** The page dots, floating over the list's bottom-right corner. */
const DOTS: React.CSSProperties = {
  position: "sticky",
  bottom: 8,
  display: "flex",
  gap: 5,
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 10px",
  margin: "0 auto",
  width: "fit-content",
  borderRadius: 999,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  pointerEvents: "none",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 12px",
  minHeight: 56,
  width: "100%",
  textAlign: "left",
  border: 0,
  borderBottom: "1px solid var(--qf-border)",
  font: "inherit",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
};

export function MobileRows({
  frames,
  config,
  meta,
  onSelectSymbol,
  chartedSymbol,
  onSymbols,
  query = "",
  onRowCounts,
  onSearchAll,
  wide = false,
}: MobileRowsProps) {
  const perLine = wide ? TABLET_COLUMNS_PER_LINE : MOBILE_COLUMNS_PER_LINE;
  const perPage = perLine * 2;
  const resolveToken = useTokenResolver();

  /**
   * Which page of columns the **whole list** is showing.
   *
   * One index here rather than one per row: rows each remembering their own
   * page would put six different columns down one screen, and a column of
   * figures that are not the same figure is worse than not showing them.
   */
  const [page, setPage] = useState(0);
  const pageCount = useMemo(() => {
    const frame = detailFrame(frames);
    return frame ? columnPages(config, frame.fields.map((f) => f.name), perPage).length : 0;
  }, [frames, config, perPage]);

  // A narrowed column set can leave the index past the end; `mobileRows` clamps
  // for the render, and this settles the state so the dots agree with it.
  useEffect(() => {
    if (pageCount > 0 && page > pageCount - 1) setPage(pageCount - 1);
  }, [pageCount, page]);

  const rows = useMemo(
    () => mobileRows(frames, config, meta, page, perPage),
    [frames, config, meta, page, perPage],
  );

  /**
   * Horizontal swipe pages the columns; vertical is left to the list.
   *
   * **The axis is decided once, on the first significant movement, and then
   * held.** Deciding per event lets a diagonal drag flip between paging and
   * scrolling mid-gesture, which reads as the list fighting the finger. Once
   * a gesture is vertical it is ignored here for its whole life, and once it is
   * horizontal the page follows on release.
   *
   * The list carries `touch-action: pan-y`, which is what makes any of this
   * reachable: at the default the browser claims both axes and cancels the
   * pointer before a horizontal drag can be classified. See LIST.
   */
  const swipe = useRef<{ id: number; x: number; y: number; axis?: "x" | "y" } | null>(null);
  const AXIS_SLOP = 12;

  const onPointerDown = (e: React.PointerEvent) => {
    swipe.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = swipe.current;
    if (!s || s.id !== e.pointerId || s.axis) return;
    const dx = Math.abs(e.clientX - s.x);
    const dy = Math.abs(e.clientY - s.y);
    if (Math.max(dx, dy) < AXIS_SLOP) return;
    s.axis = dx > dy ? "x" : "y";
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s || s.id !== e.pointerId || s.axis !== "x" || pageCount < 2) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) < SWIPE_DISTANCE) return;
    // **The row must not also open.** A drag that ends on a row still fires its
    // click, so without this a swipe would page the columns *and* chart
    // whichever symbol happened to be under the finger — the row you were
    // reading replaced by the one you were dragging across.
    swiped.current = true;
    // Left drags forward, as every pager does: the content follows the finger.
    setPage((p) => Math.max(0, Math.min(pageCount - 1, p + (dx < 0 ? 1 : -1))));
  };

  /** Set by a completed swipe, read and cleared by the next row click. */
  const swiped = useRef(false);
  const selectSymbol = (symbol: string) => {
    if (swiped.current) {
      swiped.current = false;
      return;
    }
    onSelectSymbol?.(symbol);
  };

  /**
   * Publish the visible symbols upwards.
   *
   * In an effect rather than during render because it sets state in an
   * ancestor, and `rows` is memoised so this fires when the list actually
   * changes rather than on every render of the shell.
   */
  useEffect(() => {
    onSymbols?.(rows.map((r) => ({ symbol: r.symbol, name: r.name })));
  }, [rows, onSymbols]);

  // Counted before the search, which is a way of looking rather than a filter.
  const total = useMemo(() => detailFrame(frames)?.rows.length ?? 0, [frames]);
  useEffect(() => {
    onRowCounts?.(rows.length, total);
  }, [rows.length, total, onRowCounts]);

  const shown = useMemo(() => searchRows(rows, query), [rows, query]);

  if (query.trim() && shown.length === 0) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "1rem", color: "var(--qf-text-secondary)", fontSize: "0.85rem" }}>
        <span>Nothing in this list matches “{query.trim()}”.</span>
        {onSearchAll && (
          <button
            type="button"
            onClick={() => onSearchAll(query.trim())}
            style={{ minHeight: 44, padding: "0 16px", borderRadius: 8, border: "1px solid var(--qf-border)", background: "var(--qf-surface)", color: "var(--qf-accent)", font: "inherit" }}
          >
            Search all symbols for “{query.trim()}”
          </button>
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          // `height`, not `flex`, for the same reason as LIST above: the parent
          // is a flex item and not a flex container, so a flex value here would
          // collapse this to its text and leave the notice at the very top.
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          textAlign: "center",
          color: "var(--qf-text-secondary)",
          fontSize: "0.8rem",
        }}
      >
        {/* A filter that matches nothing and a group that is still loading look
            identical from here, so the message says the one thing that is
            certainly true and reachable — the filter is in the bar above. */}
        No rows. Check the filter, or pick another group above.
      </div>
    );
  }

  return (
    <div
      style={LIST}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        swipe.current = null;
      }}
    >
      {shown.map((row, i) => (
        <Row
          key={row.symbol}
          row={row}
          alt={i % 2 === 1}
          charted={row.symbol === chartedSymbol?.toUpperCase()}
          resolveToken={resolveToken}
          onSelect={selectSymbol}
          perLine={perLine}
        />
      ))}
      {/* The page indicator, pinned over the list rather than in it: a row of
          dots that scrolled away with the rows would be unreachable exactly
          when somebody wondered which page they were on. Hidden at one page,
          where it would only ever say "1 of 1". */}
      {pageCount > 1 && (
        <div style={DOTS} aria-hidden>
          {Array.from({ length: pageCount }, (_, i) => (
            <span
              key={i}
              style={{
                width: i === page ? 14 : 6,
                height: 6,
                borderRadius: 999,
                background: i === page ? "var(--qf-accent)" : "var(--qf-axis, #2a2f3a)",
                transition: "width 140ms",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({
  row,
  alt,
  charted,
  resolveToken,
  onSelect,
  perLine,
}: {
  row: MobileRow;
  alt: boolean;
  charted: boolean;
  resolveToken: (token: string) => string;
  onSelect?: (symbol: string) => void;
  perLine: number;
}) {
  return (
    <button
      onClick={() => onSelect?.(row.symbol)}
      style={{ ...ROW, background: alt ? "var(--qf-row-alt)" : "var(--qf-background)" }}
    >
      {/*
        The logo, spanning both lines, when the workspace declares one.

        `loading="lazy"` because a group is five hundred rows and only a
        screenful is ever in view — without it the list opens by requesting
        every logo at once. A broken URL hides the element rather than leaving
        the browser's torn-image glyph, which is louder than the logo it failed
        to be; the row is still perfectly legible without it.
      */}
      {row.logoUrl && (
        <img
          src={row.logoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          style={{
            flex: "0 0 auto",
            width: LOGO_SIZE,
            height: LOGO_SIZE,
            objectFit: "contain",
            borderRadius: 4,
          }}
        />
      )}
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        {/* **The symbol carries the marker, in the accent colour.**
            A reserved dot column cost ~16px of a 390px row to say one bit, and
            it competed with the logo beside it for the same glance. Colouring
            the symbol costs no width at all, and it marks the thing the state
            is actually about — which symbol is on the chart — rather than a
            token sitting next to it. */}
        <span
          style={{
            display: "block",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: charted ? "var(--qf-accent)" : "var(--qf-text)",
          }}
        >
          {row.symbol}
        </span>
        {row.name && (
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: "var(--qf-text-muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name}
          </span>
        )}
      </span>
      {/*
        Two lines of two, mirroring the symbol/name stack opposite.

        **Fixed track widths, not `auto`.** Each row is its own grid, so `auto`
        sizes every row independently: one long value — "Communication Services"
        — widens that row's column and drags its price out of line with the row
        above. A column of figures that does not line up is the one thing that
        makes a list unscannable, which is most of why the numbers are here.

        Fixed tracks cost a truncated word occasionally; `title` carries the
        full value, and the ellipsis says there is more.
      */}
      <span
        style={{
          flex: "0 0 auto",
          display: "grid",
          gridTemplateColumns: `repeat(${perLine}, ${CELL_WIDTH}px)`,
          columnGap: 10,
          rowGap: 1,
        }}
      >
        {row.cells.map((cell, i) => (
          <span
            key={cell.field}
            title={cell.header}
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-end",
              gap: 4,
              // Right-aligned and clipped inside its own track, so a long
              // sector name cannot push the number next to it out of line.
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              // The first line is the headline pair — price and move — and the
              // second its supporting detail, the same hierarchy as the symbol
              // over the name opposite.
              fontSize: i < perLine ? 14 : 11,
              fontVariantNumeric: "tabular-nums",
              color: cell.token ? resolveToken(cell.token) : "var(--qf-text)",
            }}
          >
            {/* A label only where the number cannot speak for itself. Price and
                change are obvious from their shape — `305.92`, `+1.2%` — and
                labelling them would spend the width that lets the other two be
                identifiable at all. */}
            {cell.label && (
              <span style={{ fontSize: 9, color: "var(--qf-text-muted)" }}>{cell.label}</span>
            )}
            {cell.text}
          </span>
        ))}
      </span>
    </button>
  );
}
